import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errMsg } from "@/lib/api";

const monthKey = () => `pro-nudge-${new Date().toISOString().slice(0, 7)}`;

export const useUpgradePro = () => {
  const [busy, setBusy] = useState(false);
  const upgrade = async () => {
    setBusy(true);
    try {
      await api.post("/payments/upgrade-pro");
      toast.success("You're on Pro. Unlimited questions start now.");
      setTimeout(() => window.location.reload(), 900);
    } catch (e) {
      if (e?.response?.status === 400 && /active membership/.test(errMsg(e))) {
        try {
          const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin, item: "pro" });
          window.location.href = data.checkout_url;
          return;
        } catch (e2) { toast.error(errMsg(e2)); setBusy(false); return; }
      }
      toast.error(errMsg(e));
      setBusy(false);
    }
  };
  return [busy, upgrade];
};

export const UpgradeConfirm = ({ open, setOpen }) => {
  const [busy, upgrade] = useUpgradePro();
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent data-testid="upgrade-pro-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Upgrade to Pro for £79/month?</AlertDialogTitle>
          <AlertDialogDescription>Unlimited questions, fortnightly group calls with Lindsay and your Brand Foundations Document. It switches over today, and you only pay the difference for the rest of this month.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="upgrade-pro-cancel">Not now</AlertDialogCancel>
          <AlertDialogAction onClick={(e) => { e.preventDefault(); upgrade(); }} disabled={busy} data-testid="upgrade-pro-confirm" className="bg-pinkb hover:bg-pinkb/90">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Upgrade now
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export const ProNudge = ({ usage }) => {
  const [hidden, setHidden] = useState(() => !!localStorage.getItem(monthKey()));
  const [open, setOpen] = useState(false);
  useEffect(() => { setHidden(!!localStorage.getItem(monthKey())); }, [usage]);
  const lim = usage?.questions_limit;
  if (hidden || !usage || usage.trial || usage.tier !== "club" || !lim || usage.questions_used < 0.75 * lim || usage.questions_used >= lim) return null;
  const dismiss = () => { localStorage.setItem(monthKey(), "1"); setHidden(true); };
  return (
    <div className="fade-up mb-5 relative rounded-2xl bg-navy text-white p-6 flex flex-wrap items-center justify-between gap-4" data-testid="pro-nudge">
      <button onClick={dismiss} aria-label="Hide for this month" data-testid="pro-nudge-dismiss" className="absolute top-3 right-3 text-white/60 hover:text-white"><X className="h-4 w-4" /></button>
      <div className="max-w-xl pr-6">
        <div className="eyebrow !text-sun mb-1 flex items-center gap-2"><Sparkles className="h-3.5 w-3.5" />You're making the most of the club</div>
        <p className="font-serif text-xl">You've used most of this month's questions. Go unlimited, and join Lindsay's fortnightly group calls.</p>
      </div>
      <div className="flex items-center gap-3">
        <Link to="/app/membership" className="text-sm text-white/80 underline" data-testid="pro-nudge-compare">Compare plans</Link>
        <Button onClick={() => setOpen(true)} data-testid="pro-nudge-upgrade" className="rounded-full bg-pinkb hover:bg-pinkb/90 text-white">Upgrade to Pro</Button>
      </div>
      <UpgradeConfirm open={open} setOpen={setOpen} />
    </div>
  );
};
